import {
  DEFAULT_BACKSTAGE_QA_EMAILS,
  STAFF_LOGIN_PATH,
  STAFF_RESET_PATH,
  isPublicationPrivilegedUser,
} from '../../shared/publicationAccess.js';

export const DEFAULT_MAINTENANCE_ALLOWED_PATHS = Object.freeze([
  '/maintenance',
  STAFF_LOGIN_PATH,
  STAFF_RESET_PATH,
]);

export const MAINTENANCE_ALWAYS_ALLOWED_PREFIXES = Object.freeze([
  '/newsletter/unsubscribe',
  '/ext/twitch',
]);

export const DEFAULT_MAINTENANCE_ALLOWED_ROLES = Object.freeze(['admin']);

const SAFE_EXACT_PATHS = new Set(DEFAULT_MAINTENANCE_ALLOWED_PATHS);

const normalizeStringList = (value) =>
  (Array.isArray(value) ? value : [])
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter(Boolean);

const matchesPathPrefix = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const sanitizeMaintenanceAllowedPaths = (value) => {
  const safePaths = normalizeStringList(value).filter((path) => SAFE_EXACT_PATHS.has(path));
  return safePaths.length > 0
    ? Array.from(new Set(safePaths))
    : [...DEFAULT_MAINTENANCE_ALLOWED_PATHS];
};

export const sanitizeMaintenanceAllowedRoles = (value) => {
  const roles = normalizeStringList(value).map((role) => role.toLowerCase());
  return roles.length > 0
    ? Array.from(new Set(roles))
    : [...DEFAULT_MAINTENANCE_ALLOWED_ROLES];
};

export const isMaintenanceAllowedPath = (pathname, allowedPaths) => {
  if (typeof pathname !== 'string') {
    return false;
  }

  const safeAllowedPaths = sanitizeMaintenanceAllowedPaths(allowedPaths);
  return (
    safeAllowedPaths.includes(pathname) ||
    MAINTENANCE_ALWAYS_ALLOWED_PREFIXES.some((prefix) =>
      matchesPathPrefix(pathname, prefix),
    )
  );
};

export const isMaintenancePrivilegedUser = (
  user,
  allowedRoles = DEFAULT_MAINTENANCE_ALLOWED_ROLES,
  qaEmails = DEFAULT_BACKSTAGE_QA_EMAILS,
) => {
  const normalizedAllowedRoles = sanitizeMaintenanceAllowedRoles(allowedRoles);
  return isPublicationPrivilegedUser(user, {
    allowedRoles: normalizedAllowedRoles,
    qaEmails,
  });
};

export const resolveMaintenanceOverride = (value, { isProduction = false } = {}) => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';

  if (['on', 'true', '1'].includes(normalized)) {
    return { value: true, ignored: false, reason: 'forced_on' };
  }

  if (['off', 'false', '0'].includes(normalized)) {
    if (isProduction) {
      return { value: null, ignored: true, reason: 'forced_off_forbidden_in_production' };
    }
    return { value: false, ignored: false, reason: 'forced_off_non_production' };
  }

  return { value: null, ignored: false, reason: 'not_configured' };
};

export const normalizeMaintenanceSetting = (row) => {
  if (!row) {
    return {
      isActive: true,
      message: '',
      allowedPaths: [...DEFAULT_MAINTENANCE_ALLOWED_PATHS],
      allowedRoles: [...DEFAULT_MAINTENANCE_ALLOWED_ROLES],
      maintenanceStateAvailable: false,
    };
  }

  return {
    isActive: Boolean(row.is_active),
    message: row.message || '',
    allowedPaths: sanitizeMaintenanceAllowedPaths(row.allowed_paths),
    allowedRoles: sanitizeMaintenanceAllowedRoles(row.allowed_roles),
    maintenanceStateAvailable: true,
  };
};
